import { Component, Input, OnInit } from '@angular/core';
import { getCurrencySymbol } from '@angular/common';
import { ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import {
    CreatedEventProduct, DEFAULT_BRICK_IMAGE, EditEventPresentation, EditEventProduct, EventProductsService,
    NewEventPresentation, NewEventProduct, ZoneCatalog,
} from 'src/app/services/event-products/event-products.service';
import { DEFAULT_SECTIONS, cleanSectionName, mergeSectionNames, sectionKey } from 'src/app/shared/event-products/product-sections';
import { cleanVariantName, parseVariants, variantKey } from 'src/app/shared/event-products/product-variants';
import { formatMoney } from 'src/app/shared/event-report/event-report.model';
import { ImportZone, normalizeText } from 'src/app/shared/product-import/product-import.model';
import { PickedProductImage, ProductImagePickerComponent } from '../product-image-picker/product-image-picker.component';

type Step = 'form' | 'saving' | 'done' | 'copying';
type ImageState = 'keep' | 'upload' | 'pick' | 'clear';

/** Una presentación mientras se edita: todo texto, se valida al guardar. */
interface PresentationDraft {
    id: number;
    /** $key en Firestore; vacío si es nueva. */
    key: string;
    /** "Botella", "Vaso", "Unidad"… (al editar, la etiqueta completa). */
    name: string;
    /** Medida del catálogo ("750ML") o vacío. */
    unit: string;
    price: string;
    inventory: string;
    main: boolean;
    visible: boolean;
}

/** Lo que devuelve el modal al cerrarse tras crear o editar. */
export interface AddEventProductResult {
    created: boolean;
    updated: boolean;
    key: string;
    name: string;
    section: string;
    /** Nombres de las barras donde además se creó una copia (solo al crear). */
    copiedTo: string[];
    /** Campos de la fila de la tabla de la zona, para refrescarla sin recargar (solo al editar). */
    row: any;
}

/** Lado máximo al que se reduce una foto antes de subirla: la caja carga todas las fotos, a veces en 3G. */
const IMAGE_MAX_SIDE = 1000;

/**
 * Alta de un producto en una barra de evento. Sustituye al formulario de
 * tienda (marca, línea, matriz, nivel, categoría base, vino…) que exigía
 * campos que en un evento no significan nada. Aquí solo son obligatorios el
 * nombre y una presentación con precio; el resto es opcional.
 *
 * La foto puede subirse o tomarse de un producto que ya existe: en eventos
 * se vuelve a crear una y otra vez lo mismo (otra barra, otro precio) y la
 * foto ya está en algún lado. Al guardar se ofrece crear una copia del
 * producto en otras barras del evento, con la misma lógica que "Copiar a
 * zona".
 *
 * Con `brick` es el mismo formulario en modo edición: carga el producto y
 * sus presentaciones, y al guardar actualiza en vez de crear (sin paso de
 * copia). Así crear y editar se ven y se usan igual.
 */
@Component({
    selector: 'app-add-event-product',
    templateUrl: './add-event-product.component.html',
    styleUrls: ['./add-event-product.component.scss'],
})
export class AddEventProductComponent implements OnInit {

    @Input() zone: ImportZone = { key: '', name: '' };
    @Input() eventKey: string = '';
    /** Lo que se escribió en "Nombre del nuevo producto" antes de abrir el modal. */
    @Input() initialName: string = '';
    /** Cuántos productos tiene ya la zona: el nuevo queda al final del orden. */
    @Input() position: number = 0;
    /** Producto existente (doc con $key): activa el modo edición. */
    @Input() brick: any = null;

    step: Step = 'form';
    error = '';
    /** Leyendo el producto y sus presentaciones (solo al editar). */
    loading = false;
    /** Tras el primer intento de guardar se muestran los errores en línea. */
    submitted = false;

    // ── Datos ──────────────────────────────────────────────────────────────
    name = '';
    supplier = '';
    code = '';
    description = '';
    /** Sección con la que la caja agrupa; texto libre con sugerencias. */
    section = '';
    sectionOptions: string[] = DEFAULT_SECTIONS.slice();
    /** Se prepara en cocina: la orden aparece en la pantalla de cocina (KDS). */
    kitchen = false;
    /**
     * Variantes: alternativas del mismo producto que la caja pregunta al
     * vender ("Whiskey" en un Squeeze). El original no se lista: siempre se
     * ofrece. Ver shared/event-products/product-variants.
     */
    variants: string[] = [];
    /** Lo que se está escribiendo en "Agregar variante". */
    variantDraft = '';
    variantError = '';
    visible = true;
    presentations: PresentationDraft[] = [];
    /** Presentaciones existentes que se quitaron: se borran al guardar. */
    private removedKeys: string[] = [];
    units: string[] = [];
    suppliers: string[] = [];
    supplierFocused = false;
    private nextId = 1;

    // ── Imagen ─────────────────────────────────────────────────────────────
    /** Lo que se ve en la vista previa (data URL o URL de otro producto). */
    imagePreview = '';
    /** Archivo elegido, ya reducido, listo para subir. */
    imageDataUrl: string | null = null;
    /** Imagen reutilizada de otro producto. */
    imageUrl = '';
    imageFrom = '';
    imageFromName = '';
    imageBusy = false;
    /** Qué pasa con la foto al guardar (al crear, 'keep' sin vista previa = botella genérica). */
    imageState: ImageState = 'keep';

    // ── Tras guardar ───────────────────────────────────────────────────────
    created: CreatedEventProduct | null = null;
    /** El usuario dijo que sí quiere copiar: se muestran las barras. */
    choosingZones = false;
    zones: ImportZone[] = [];
    zonesLoaded = false;
    selectedZones: { [zoneKey: string]: boolean } = {};
    copyInventory = true;
    copyResult: { created: number; skipped: number; zones: string[] } | null = null;
    private catalogs: { [zoneKey: string]: ZoneCatalog } = {};
    private loadingCatalog: { [zoneKey: string]: boolean } = {};

    constructor(
        private modalCtrl: ModalController,
        private api: ApiService,
        public auth: AuthService,
        private service: EventProductsService,
    ) { }

    ngOnInit() {
        this.loadUnits();
        this.loadSuppliers();
        this.loadSections();
        if (this.isEdit) {
            this.loadForEdit();
            return;
        }
        this.name = String(this.initialName || '').trim();
        this.presentations = [this.newDraft('Unidad', true)];
    }

    get isEdit(): boolean {
        return !!(this.brick && this.brick.$key);
    }

    /** Carga el producto y sus presentaciones en el formulario. */
    private async loadForEdit() {
        this.loading = true;
        try {
            const source = await this.service.getProductForEdit(this.brick.$key);
            const doc = source.brick || {};
            // La página solo manda la llave; el resto (imagen actual, etc.) sale del doc.
            this.brick = { ...doc, $key: this.brick.$key };
            this.name = String(doc.name || '').trim();
            this.supplier = String(doc.supplier || '').trim();
            this.code = String(doc.system_code || (Array.isArray(doc.bar_code) ? doc.bar_code[0] : '') || '').trim();
            this.description = String(doc.description || '').trim();
            this.section = cleanSectionName(doc.section);
            this.kitchen = doc.kitchen === true;
            this.variants = parseVariants(doc.variants);
            this.visible = doc.visible !== false;
            const url = doc.image && doc.image.downloadURL ? String(doc.image.downloadURL) : '';
            this.imagePreview = url && url !== DEFAULT_BRICK_IMAGE ? url : '';
            this.imageState = 'keep';
            const list = source.presentations
                .slice()
                .sort((a, b) => (b.main === true ? 1 : 0) - (a.main === true ? 1 : 0))
                .map(p => this.draftFrom(p));
            if (list.length > 0 && !list.some(p => p.main)) list[0].main = true;
            this.presentations = list.length > 0 ? list : [this.newDraft('Unidad', true)];
        } catch (e) {
            console.error('No se pudo leer el producto:', e);
            this.error = 'No se pudo leer el producto. Cierra e intenta de nuevo.';
        }
        this.loading = false;
    }

    private draftFrom(p: any): PresentationDraft {
        return {
            id: this.nextId++,
            key: String(p.$key || ''),
            name: String(p.presentation || '').trim(),
            unit: '',
            price: p.price === null || p.price === undefined ? '' : String(p.price),
            inventory: p.inventory === null || p.inventory === undefined ? '' : String(p.inventory),
            main: p.main === true,
            visible: p.visible !== false,
        };
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  CATÁLOGOS (medidas y proveedores): solo sugerencias, nunca bloquean
    // ═══════════════════════════════════════════════════════════════════════

    private async loadUnits() {
        try {
            const snap = await this.api.getRef('catalogue').ref.where('type', '==', 'unit_system').get();
            const names: string[] = [];
            snap.forEach(doc => {
                const name = String((doc.data() || {}).name || '').trim();
                if (name && names.indexOf(name) < 0) names.push(name);
            });
            this.units = names.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
        } catch (e) {
            console.warn('No se pudieron leer las medidas:', e);
        }
    }

    private async loadSuppliers() {
        try {
            const snap = await this.api.getRef('suppliers').ref
                .where('countryCode', '==', this.auth.countryCode)
                .where('active', '==', true)
                .get();
            const names: string[] = [];
            snap.forEach(doc => {
                const name = String((doc.data() || {}).name || '').trim();
                if (name && names.indexOf(name) < 0) names.push(name);
            });
            this.suppliers = names.sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
        } catch (e) {
            console.warn('No se pudieron leer los proveedores:', e);
        }
    }

    /** Las secciones que ya usa el evento van primero; luego las sugeridas. */
    private async loadSections() {
        try {
            const used = await this.service.getEventSections(this.eventKey);
            this.sectionOptions = mergeSectionNames(used, DEFAULT_SECTIONS);
        } catch (e) {
            console.warn('No se pudieron leer las secciones del evento:', e);
        }
    }

    isSection(name: string): boolean {
        return !!cleanSectionName(this.section) && sectionKey(name) === sectionKey(this.section);
    }

    /** Tocar la sección elegida la quita: así se vuelve a "sin sección" sin borrar texto. */
    toggleSection(name: string) {
        this.section = this.isSection(name) ? '' : name;
    }

    /** Proveedores del catálogo que coinciden con lo escrito (o todos si no hay nada). */
    get supplierSuggestions(): string[] {
        const term = normalizeText(this.supplier);
        const list = term
            ? this.suppliers.filter(s => normalizeText(s).indexOf(term) >= 0 && normalizeText(s) !== term)
            : this.suppliers;
        return list.slice(0, 8);
    }

    pickSupplier(name: string) {
        this.supplier = name;
        this.supplierFocused = false;
    }

    /** El blur llega antes que el clic en la sugerencia: se espera un instante. */
    onSupplierBlur() {
        setTimeout(() => this.supplierFocused = false, 180);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  VARIANTES
    // ═══════════════════════════════════════════════════════════════════════

    /** Pasa lo escrito a la lista; repetida (misma clave) o vacía, no se agrega. */
    addVariant() {
        const name = cleanVariantName(this.variantDraft);
        if (!name) return;
        if (this.variants.some(v => variantKey(v) === variantKey(name))) {
            this.variantError = `"${name}" ya está en la lista.`;
            return;
        }
        this.variants = this.variants.concat(name);
        this.variantDraft = '';
        this.variantError = '';
    }

    removeVariant(name: string) {
        this.variants = this.variants.filter(v => v !== name);
        this.variantError = '';
    }

    get hasVariantDraft(): boolean {
        return !!cleanVariantName(this.variantDraft);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  PRESENTACIONES
    // ═══════════════════════════════════════════════════════════════════════

    private newDraft(name: string, main: boolean): PresentationDraft {
        return { id: this.nextId++, key: '', name, unit: '', price: '', inventory: '', main, visible: true };
    }

    addPresentation() {
        this.presentations.push(this.newDraft('', this.presentations.length === 0));
    }

    removePresentation(draft: PresentationDraft) {
        this.presentations = this.presentations.filter(p => p.id !== draft.id);
        if (draft.key) this.removedKeys.push(draft.key);
        if (this.presentations.length > 0 && !this.presentations.some(p => p.main)) {
            this.presentations[0].main = true;
        }
    }

    toggleVisible(draft: PresentationDraft) {
        draft.visible = !draft.visible;
    }

    setMain(draft: PresentationDraft) {
        this.presentations.forEach(p => p.main = p.id === draft.id);
    }

    /** "Botella 750ML": lo que verá la caja y el cliente. */
    labelOf(draft: PresentationDraft): string {
        return [String(draft.name || '').trim(), String(draft.unit || '').trim()].filter(Boolean).join(' ');
    }

    /**
     * Acepta "8", "8.50", "8,50" o "$8.50"; null si no es un número. Solo se
     * quita el símbolo de moneda al inicio: una letra colada ("1o0") tiene
     * que dar error y no convertirse en otro precio en silencio.
     */
    parsePrice(value: string): number | null {
        const clean = String(value === null || value === undefined ? '' : value)
            .trim()
            .replace(/^[^\d.,-]+/, '')
            .replace(/\s+/g, '')
            .replace(',', '.');
        if (!clean) return null;
        const num = Number(clean);
        return isFinite(num) && num >= 0 ? num : null;
    }

    /** Entero ≥ 0; vacío cuenta como 0. */
    parseInventory(value: string): number | null {
        const clean = String(value === null || value === undefined ? '' : value).trim();
        if (!clean) return 0;
        if (!/^\d+$/.test(clean)) return null;
        return Number(clean);
    }

    /** Texto del error de una presentación, vacío si está bien. */
    presentationIssue(draft: PresentationDraft): string {
        if (!this.labelOf(draft)) return 'Escribe el nombre de la presentación.';
        if (this.parsePrice(draft.price) === null) return 'El precio tiene que ser un número (usa punto para decimales).';
        if (this.parseInventory(draft.inventory) === null) return 'El inventario tiene que ser un número entero.';
        const label = normalizeText(this.labelOf(draft));
        if (this.presentations.some(p => p.id !== draft.id && normalizeText(this.labelOf(p)) === label)) {
            return 'Hay otra presentación con este mismo nombre.';
        }
        return '';
    }

    isFree(draft: PresentationDraft): boolean {
        return this.parsePrice(draft.price) === 0;
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  IMAGEN
    // ═══════════════════════════════════════════════════════════════════════

    async onFileInput(event: any) {
        const input = event && event.target;
        const file: File = input && input.files && input.files[0];
        if (input) input.value = '';
        if (!file) return;
        if (!/^image\//i.test(file.type || '')) {
            this.error = 'El archivo tiene que ser una imagen (JPG, PNG o WEBP).';
            return;
        }
        this.imageBusy = true;
        this.error = '';
        try {
            const dataUrl = await this.shrinkImage(file);
            this.imageDataUrl = dataUrl;
            this.imagePreview = dataUrl;
            this.imageUrl = '';
            this.imageFrom = '';
            this.imageFromName = '';
            this.imageState = 'upload';
        } catch (e) {
            console.error('No se pudo leer la imagen:', e);
            this.error = 'No se pudo leer esa imagen. Prueba con otro archivo.';
        }
        this.imageBusy = false;
    }

    /**
     * Reduce la foto al lado máximo y la devuelve como data URL. Si el
     * navegador no coopera (canvas bloqueado, formato raro) se sube tal cual.
     */
    private shrinkImage(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => {
                const original = String(reader.result || '');
                const img = new Image();
                img.onerror = () => resolve(original);
                img.onload = () => {
                    try {
                        const scale = Math.min(1, IMAGE_MAX_SIDE / Math.max(img.width, img.height));
                        if (scale >= 1) return resolve(original);
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.round(img.width * scale);
                        canvas.height = Math.round(img.height * scale);
                        const ctx = canvas.getContext('2d');
                        if (!ctx) return resolve(original);
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        // PNG conserva la transparencia de las botellas recortadas; lo demás va a JPG.
                        const isPng = /png$/i.test(file.type || '');
                        resolve(isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.86));
                    } catch (e) {
                        resolve(original);
                    }
                };
                img.src = original;
            };
            reader.readAsDataURL(file);
        });
    }

    async openImagePicker() {
        const modal = await this.modalCtrl.create({
            component: ProductImagePickerComponent,
            cssClass: 'productImagePickerModal',
            componentProps: {
                eventKey: this.eventKey,
                initialQuery: this.name,
            },
        });
        modal.onDidDismiss().then((res: any) => {
            const picked: PickedProductImage = res && res.data;
            if (!picked || !picked.url) return;
            this.imageUrl = picked.url;
            this.imagePreview = picked.url;
            this.imageFrom = picked.key || '';
            this.imageFromName = picked.name || '';
            this.imageDataUrl = null;
            this.imageState = 'pick';
        });
        await modal.present();
    }

    /** Al editar, quitar la foto vuelve a la botella genérica al guardar. */
    clearImage() {
        this.imagePreview = '';
        this.imageDataUrl = null;
        this.imageUrl = '';
        this.imageFrom = '';
        this.imageFromName = '';
        this.imageState = this.isEdit ? 'clear' : 'keep';
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  GUARDAR
    // ═══════════════════════════════════════════════════════════════════════

    get nameMissing(): boolean {
        return !String(this.name || '').trim();
    }

    get formIssues(): string[] {
        const issues: string[] = [];
        if (this.nameMissing) issues.push('Escribe el nombre del producto.');
        if (this.presentations.length === 0) issues.push('Agrega al menos una presentación con su precio.');
        this.presentations.forEach(p => {
            const issue = this.presentationIssue(p);
            if (issue) issues.push(`${this.labelOf(p) || 'Presentación'}: ${issue}`);
        });
        return issues;
    }

    get isValid(): boolean {
        return this.formIssues.length === 0;
    }

    /** Resumen para el botón de guardar: "Botella 750ML · $8.00". */
    get mainSummary(): string {
        const main = this.presentations.filter(p => p.main)[0] || this.presentations[0];
        if (!main) return '';
        const price = this.parsePrice(main.price);
        return price === null ? this.labelOf(main) : `${this.labelOf(main)} · ${this.formatMoney(price)}`;
    }

    /** "$8.50": el mismo símbolo que usa el pipe currency con auth.currency (código o símbolo). */
    formatMoney(value: number): string {
        return formatMoney(value, this.currencySymbol);
    }

    get currencySymbol(): string {
        const code = String(this.auth.currency || '').trim() || 'USD';
        try {
            return getCurrencySymbol(code, 'narrow', 'en-US') || code;
        } catch (e) {
            return code;
        }
    }

    private buildInput(): NewEventProduct {
        const presentations: NewEventPresentation[] = this.presentations.map(p => ({
            name: this.labelOf(p),
            price: this.parsePrice(p.price) || 0,
            inventory: this.parseInventory(p.inventory) || 0,
            visible: p.visible !== false,
            main: p.main,
        }));
        return {
            zoneKey: this.zone.key,
            eventKey: this.eventKey,
            name: this.name.trim(),
            supplier: this.supplier.trim(),
            code: this.code.trim(),
            description: this.description.trim(),
            section: cleanSectionName(this.section),
            kitchen: this.kitchen,
            variants: this.variants.slice(),
            visible: this.visible,
            position: Number(this.position) || 0,
            presentations,
            imageUrl: this.imageUrl,
            imageFrom: this.imageFrom,
        };
    }

    private buildEditInput(): EditEventProduct {
        const presentations: EditEventPresentation[] = this.presentations.map(p => ({
            key: p.key,
            name: this.labelOf(p),
            price: this.parsePrice(p.price) || 0,
            inventory: this.parseInventory(p.inventory) || 0,
            visible: p.visible !== false,
            main: p.main,
        }));
        return {
            key: this.brick.$key,
            zoneKey: this.zone.key,
            name: this.name.trim(),
            supplier: this.supplier.trim(),
            code: this.code.trim(),
            description: this.description.trim(),
            section: cleanSectionName(this.section),
            kitchen: this.kitchen,
            variants: this.variants.slice(),
            visible: this.visible,
            presentations,
            removedPresentationKeys: this.removedKeys.slice(),
            image: this.imageState,
            imageUrl: this.imageUrl,
            imageFrom: this.imageFrom,
        };
    }

    async save() {
        if (this.step !== 'form' || this.loading) return;
        // Una variante escrita sin tocar "Agregar" también vale.
        if (this.hasVariantDraft) this.addVariant();
        this.submitted = true;
        if (!this.isValid) {
            this.error = 'Revisa los campos marcados antes de guardar.';
            return;
        }
        this.error = '';
        this.step = 'saving';
        try {
            if (this.isEdit) {
                const saved = await this.service.updateProduct(this.buildEditInput(), this.imageDataUrl);
                this.finishEdit(saved);
                return;
            }
            this.created = await this.service.createProduct(this.buildInput(), this.imageDataUrl);
            this.step = 'done';
            this.loadZones();
        } catch (e) {
            console.error('No se pudo guardar el producto:', e);
            this.error = 'No se pudo guardar el producto. Revisa la conexión e intenta de nuevo.';
            this.step = 'form';
        }
    }

    /** Cierra devolviendo lo que la tabla de la zona muestra de este producto. */
    private finishEdit(saved: CreatedEventProduct) {
        const main = this.presentations.filter(p => p.main)[0] || this.presentations[0];
        const imageUrl = saved.imageUrl || (this.brick.image && this.brick.image.downloadURL) || '';
        const result: AddEventProductResult = {
            created: false,
            updated: true,
            key: saved.key,
            name: saved.name,
            section: cleanSectionName(this.section),
            copiedTo: [],
            row: {
                name: saved.name,
                supplier: this.supplier.trim(),
                productCode: this.code.trim(),
                price: main ? (this.parsePrice(main.price) || 0) : 0,
                inventory: main ? (this.parseInventory(main.inventory) || 0) : 0,
                visible: this.visible,
                variants: this.variants.slice(),
                image: { downloadURL: imageUrl },
            },
        };
        this.modalCtrl.dismiss(result);
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  COPIA A OTRAS BARRAS
    // ═══════════════════════════════════════════════════════════════════════

    private async loadZones() {
        try {
            const all = await this.service.getEventZones(this.eventKey, this.zone);
            this.zones = all.filter(z => z.key !== this.zone.key);
        } catch (e) {
            console.error('No se pudieron leer las zonas del evento:', e);
            this.zones = [];
        }
        this.zonesLoaded = true;
    }

    startCopy() {
        this.choosingZones = true;
    }

    async toggleZone(zone: ImportZone, checked: boolean) {
        if (!!this.selectedZones[zone.key] === !!checked) return;
        this.selectedZones = { ...this.selectedZones, [zone.key]: !!checked };
        if (checked && !this.catalogs[zone.key] && !this.loadingCatalog[zone.key]) {
            this.loadingCatalog[zone.key] = true;
            try {
                this.catalogs[zone.key] = await this.service.getZoneCatalog(zone.key);
            } catch (e) {
                console.error('No se pudo leer la zona destino:', e);
                this.error = `No se pudo leer ${zone.name}. Intenta de nuevo.`;
                this.selectedZones = { ...this.selectedZones, [zone.key]: false };
            }
            this.loadingCatalog[zone.key] = false;
        }
    }

    isLoadingZone(zone: ImportZone): boolean {
        return !!this.loadingCatalog[zone.key];
    }

    catalogSize(zone: ImportZone): number {
        return this.catalogs[zone.key] ? this.catalogs[zone.key].count : -1;
    }

    /** Ya hay un producto con este nombre en esa barra: la copia se omite. */
    existsIn(zone: ImportZone): boolean {
        const catalog = this.catalogs[zone.key];
        if (!catalog || !this.created) return false;
        const name = normalizeText(this.created.name);
        return catalog.bricks.some(b => normalizeText(b.name) === name);
    }

    get destinationZones(): ImportZone[] {
        return this.zones.filter(z => this.selectedZones[z.key]);
    }

    /** Barras elegidas donde de verdad se va a crear la copia. */
    get copyTargets(): ImportZone[] {
        return this.destinationZones.filter(z => !!this.catalogs[z.key] && !this.existsIn(z));
    }

    get canCopy(): boolean {
        return this.step === 'done' && this.copyTargets.length > 0
            && this.destinationZones.every(z => !!this.catalogs[z.key]);
    }

    get copyLabel(): string {
        const n = this.copyTargets.length;
        if (n === 0) return 'Crear copia';
        if (n === 1) return `Crear copia en ${this.copyTargets[0].name}`;
        return `Crear copia en ${n} barras`;
    }

    async runCopy() {
        if (!this.canCopy || !this.created) return;
        const targets = this.copyTargets;
        const positions: { [zoneKey: string]: number } = {};
        targets.forEach(z => positions[z.key] = this.catalogs[z.key].count);
        const plan = this.service.planCopy([{ $key: this.created.key, name: this.created.name }], targets, this.catalogs, 'skip');

        this.step = 'copying';
        this.error = '';
        try {
            const result = await this.service.copyProducts(
                plan,
                { sourceZone: this.zone, eventKey: this.eventKey, copyInventory: this.copyInventory },
                positions,
            );
            this.copyResult = {
                created: result.created,
                skipped: result.skipped,
                zones: plan.filter(i => i.action === 'create').map(i => i.zone.name),
            };
        } catch (e) {
            console.error('Falló la copia:', e);
            this.error = 'No se pudo copiar a todas las barras. Revisa la conexión e intenta de nuevo.';
            // Se releen los destinos para reflejar lo que sí se escribió.
            for (const z of targets) {
                try {
                    this.catalogs[z.key] = await this.service.getZoneCatalog(z.key);
                } catch (err) { /* se queda la lectura anterior */ }
            }
        }
        this.step = 'done';
    }

    // ═══════════════════════════════════════════════════════════════════════
    //  CERRAR
    // ═══════════════════════════════════════════════════════════════════════

    get busy(): boolean {
        return this.step === 'saving' || this.step === 'copying';
    }

    finish() {
        if (!this.created) return this.modalCtrl.dismiss();
        const result: AddEventProductResult = {
            created: true,
            updated: false,
            key: this.created.key,
            name: this.created.name,
            section: cleanSectionName(this.section),
            copiedTo: this.copyResult ? this.copyResult.zones : [],
            row: null,
        };
        this.modalCtrl.dismiss(result);
    }

    close() {
        if (this.busy) return;
        if (this.created) return this.finish();
        this.modalCtrl.dismiss();
    }
}
